import type {
  ConnectorCategory,
  ConnectorId,
  ConnectorSnapshot,
  ConnectorStatus,
} from "./connectors/types";

export type SourceAuthorityNodeKind =
  | "human"
  | "account"
  | "website"
  | "media"
  | "institution"
  | "market"
  | "community";

export type SourceAuthorityRelationshipKind =
  | "operates"
  | "hosts"
  | "authors"
  | "contributes_to"
  | "leads"
  | "founded"
  | "publishes";

export type SourceAuthorityEvidence = {
  type: "first_party_surface" | "official_role" | "registry_mapping";
  label: string;
  url: string;
  observedAt: string;
};

export type SourceAuthorityNode = {
  id: string;
  label: string;
  kind: SourceAuthorityNodeKind;
  category: ConnectorCategory;
  connectorId: ConnectorId | null;
  parentConnectorId: ConnectorId | null;
  url: string;
  status: ConnectorStatus;
  description: string;
};

export type SourceAuthorityRelationship = {
  id: string;
  from: string;
  to: string;
  kind: SourceAuthorityRelationshipKind;
  confidence: "high" | "medium" | "low";
  state: "registered" | "historical";
  rationale: string;
  evidence: SourceAuthorityEvidence[];
};

export type SourceAuthorityGraph = {
  schemaVersion: 1;
  nodes: SourceAuthorityNode[];
  relationships: SourceAuthorityRelationship[];
  coverage: {
    humanNodeCount: number;
    linkedHumanNodeCount: number;
    unlinkedHumanNodeIds: string[];
  };
};

export const emptySourceAuthorityGraph: SourceAuthorityGraph = {
  schemaVersion: 1,
  nodes: [],
  relationships: [],
  coverage: {
    humanNodeCount: 0,
    linkedHumanNodeCount: 0,
    unlinkedHumanNodeIds: [],
  },
};

export const sourceAuthorityNodeKindLabels: Record<SourceAuthorityNodeKind, string> = {
  human: "Human",
  account: "Account",
  website: "Website",
  media: "Media source",
  institution: "Institution",
  market: "Market source",
  community: "Community",
};

export const sourceAuthorityRelationshipLabels: Record<
  SourceAuthorityRelationshipKind,
  { outbound: string; inbound: string }
> = {
  operates: { outbound: "Operates", inbound: "Operated by" },
  hosts: { outbound: "Hosts", inbound: "Hosted by" },
  authors: { outbound: "Authors", inbound: "Authored by" },
  contributes_to: { outbound: "Contributes to", inbound: "Contributor" },
  leads: { outbound: "Leads", inbound: "Led by" },
  founded: { outbound: "Founded", inbound: "Founded by" },
  publishes: { outbound: "Publishes", inbound: "Published by" },
};

const REGISTERED_AT = "2026-08-27";

type RelationshipSeed = {
  from: ConnectorId;
  to: ConnectorId;
  kind: Exclude<SourceAuthorityRelationshipKind, "operates">;
  confidence: SourceAuthorityRelationship["confidence"];
  state?: SourceAuthorityRelationship["state"];
  rationale: string;
  evidenceSide?: "from" | "to";
};

// These are deliberately typed role and publishing relationships, not a generic
// claim that every output was personally authored by the person above it.
const relationshipSeeds: RelationshipSeed[] = [
  { from: "peter-diamandis", to: "moonshots", kind: "hosts", confidence: "high", rationale: "The source registry identifies Peter Diamandis as the Moonshots host." },
  { from: "alex-wissner-gross", to: "moonshots", kind: "contributes_to", confidence: "medium", rationale: "The source registry identifies Alex Wissner-Gross as a Moonshots Mate." },
  { from: "salim-ismail", to: "moonshots", kind: "contributes_to", confidence: "medium", rationale: "The source registry identifies Salim Ismail as a Moonshots Mate." },
  { from: "david-blundin", to: "moonshots", kind: "contributes_to", confidence: "medium", rationale: "The source registry identifies David Blundin as a Moonshots Mate." },
  { from: "chamath-palihapitiya", to: "all-in", kind: "hosts", confidence: "high", rationale: "The source registry identifies Chamath Palihapitiya as an All-In host." },
  { from: "jason-calacanis", to: "all-in", kind: "hosts", confidence: "high", rationale: "The source registry identifies Jason Calacanis as an All-In host." },
  { from: "david-sacks", to: "all-in", kind: "hosts", confidence: "high", rationale: "The source registry identifies David Sacks as an All-In host." },
  { from: "david-friedberg", to: "all-in", kind: "hosts", confidence: "high", rationale: "The source registry identifies David Friedberg as an All-In host." },
  { from: "jiang-xueqin", to: "predictive-history-youtube", kind: "hosts", confidence: "high", rationale: "The source registry identifies Jiang Xueqin as the creator and host of Predictive History." },
  { from: "jiang-xueqin", to: "predictive-history-substack", kind: "authors", confidence: "high", rationale: "The registered publication is the written Predictive History source associated with its creator." },
  { from: "naval-ravikant", to: "naval-youtube", kind: "hosts", confidence: "high", rationale: "The source registry identifies Naval Ravikant as the creator and host of the Naval Podcast." },
  { from: "naval-ravikant", to: "naval-blog", kind: "authors", confidence: "high", rationale: "The registered Naval blog is a first-party publication source." },
  { from: "naval-ravikant", to: "naval-substack", kind: "authors", confidence: "high", rationale: "The registered Naval Substack is a first-party publication source." },
  { from: "john-coogan", to: "tbpn-youtube", kind: "hosts", confidence: "high", rationale: "The source registry identifies John Coogan as a TBPN co-founder and host." },
  { from: "jordi-hays", to: "tbpn-youtube", kind: "hosts", confidence: "high", rationale: "The source registry identifies Jordi Hays as a TBPN co-founder and host." },

  { from: "elon-musk", to: "tesla", kind: "leads", confidence: "medium", rationale: "The source registry associates Elon Musk with an operator role at Tesla." },
  { from: "elon-musk", to: "spacex", kind: "founded", confidence: "medium", rationale: "The source registry associates Elon Musk with a founder and operator role at SpaceX." },
  { from: "elon-musk", to: "spacexai", kind: "leads", confidence: "medium", rationale: "The source registry associates Elon Musk with the registered SpaceXAI institution." },
  { from: "sam-altman", to: "openai", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Sam Altman as OpenAI co-founder and CEO.", evidenceSide: "from" },
  { from: "greg-brockman", to: "openai", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Greg Brockman as OpenAI co-founder and President.", evidenceSide: "from" },
  { from: "fidji-simo", to: "openai", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Fidji Simo as OpenAI CEO of Applications.", evidenceSide: "from" },
  { from: "mark-chen", to: "openai", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Mark Chen as OpenAI Chief Research Officer.", evidenceSide: "from" },
  { from: "jakub-pachocki", to: "openai", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Jakub Pachocki as OpenAI Chief Scientist.", evidenceSide: "from" },
  { from: "brad-lightcap", to: "openai", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Brad Lightcap as OpenAI Chief Operating Officer.", evidenceSide: "from" },
  { from: "dario-amodei", to: "anthropic", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Dario Amodei as Anthropic co-founder and CEO.", evidenceSide: "from" },
  { from: "daniela-amodei", to: "anthropic", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Daniela Amodei as Anthropic co-founder and President.", evidenceSide: "from" },
  { from: "jared-kaplan", to: "anthropic", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Jared Kaplan as Anthropic co-founder and Chief Science Officer.", evidenceSide: "from" },
  { from: "rahul-patil", to: "anthropic", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Rahul Patil as Anthropic Chief Technology Officer.", evidenceSide: "from" },
  { from: "krishna-rao", to: "anthropic", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Krishna Rao as Anthropic Chief Financial Officer.", evidenceSide: "from" },
  { from: "jack-clark", to: "anthropic", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Jack Clark as Anthropic co-founder and Head of Public Benefit.", evidenceSide: "from" },
  { from: "mike-krieger", to: "anthropic", kind: "leads", confidence: "high", rationale: "The registered official role source identifies Mike Krieger as the Anthropic Labs leader.", evidenceSide: "from" },
  { from: "brad-gerstner", to: "altimeter-capital", kind: "leads", confidence: "high", rationale: "The incentive dossier's regulatory evidence identifies Brad Gerstner as Altimeter founder, CEO, and managing principal.", evidenceSide: "from" },
  { from: "jim-simons", to: "renaissance-technologies", kind: "founded", confidence: "high", state: "historical", rationale: "The registered historical record identifies Jim Simons as Renaissance Technologies founder.", evidenceSide: "from" },
  { from: "warren-buffett", to: "berkshire-hathaway", kind: "leads", confidence: "high", rationale: "The registered first-party record identifies Warren Buffett as Berkshire Hathaway chairman.", evidenceSide: "from" },

  { from: "openai", to: "openai-youtube", kind: "publishes", confidence: "high", rationale: "OpenAI's registered official-source directory includes its YouTube channel." },
  { from: "openai", to: "openai-news", kind: "publishes", confidence: "high", rationale: "OpenAI News is registered as an official OpenAI publication surface." },
  { from: "openai", to: "openai-podcast", kind: "publishes", confidence: "high", rationale: "The OpenAI Podcast is registered as an official OpenAI media surface." },
  { from: "anthropic", to: "anthropic-youtube", kind: "publishes", confidence: "high", rationale: "Anthropic's registered official sources include its YouTube channel." },
  { from: "anthropic", to: "anthropic-news", kind: "publishes", confidence: "high", rationale: "Anthropic Newsroom is registered as an official Anthropic publication surface." },
  { from: "tesla", to: "tesla-media", kind: "publishes", confidence: "high", rationale: "Tesla Official Media is registered under the Tesla institution." },
  { from: "spacex", to: "spacex-media", kind: "publishes", confidence: "high", rationale: "SpaceX Official Media is registered under the SpaceX institution." },
  { from: "apple", to: "apple-media", kind: "publishes", confidence: "high", rationale: "Apple Official Media is registered under the Apple institution." },
  { from: "microsoft", to: "microsoft-media", kind: "publishes", confidence: "high", rationale: "Microsoft Official Media is registered under the Microsoft institution." },
  { from: "alphabet-google", to: "google-media", kind: "publishes", confidence: "high", rationale: "Google Official Media is registered under Alphabet / Google." },
  { from: "amazon", to: "amazon-media", kind: "publishes", confidence: "high", rationale: "Amazon Official Media is registered under the Amazon institution." },
  { from: "meta", to: "meta-media", kind: "publishes", confidence: "high", rationale: "Meta Official Media is registered under the Meta institution." },
  { from: "nvidia", to: "nvidia-media", kind: "publishes", confidence: "high", rationale: "NVIDIA Official Media is registered under the NVIDIA institution." },
  { from: "spacexai", to: "spacexai-media", kind: "publishes", confidence: "high", rationale: "SpaceXAI Official Media is registered under the SpaceXAI institution." },
];

function connectorNodeKind(connector: ConnectorSnapshot): SourceAuthorityNodeKind {
  if (connector.category === "person") return "human";
  if (connector.category === "institution") return "institution";
  if (connector.category === "market") return "market";
  if (connector.id === "wallstreetbets-reddit") return "community";
  return "media";
}

function profileIsPublishingSurface(
  profile: NonNullable<ConnectorSnapshot["person"]>["profiles"][number],
) {
  return profile.platform !== "Official" || /personal site/i.test(profile.label);
}

function surfaceId(connectorId: ConnectorId, platform: string) {
  return `source-surface:${connectorId}:${platform.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

function profileLabel(
  personName: string,
  profile: NonNullable<ConnectorSnapshot["person"]>["profiles"][number],
) {
  if (profile.platform === "Official") return profile.label;
  if (profile.label.startsWith("@")) return `${profile.label} on ${profile.platform}`;
  return `${personName} on ${profile.platform}`;
}

function relationshipId(from: string, kind: SourceAuthorityRelationshipKind, to: string) {
  return `source-relation:${from}:${kind}:${to}`;
}

function firstRoleEvidence(connector: ConnectorSnapshot) {
  return connector.person?.profiles.find((profile) => profile.platform === "Official")
    ?? connector.person?.profiles[0];
}

export function sourceRelationshipLabel(
  relationship: Pick<SourceAuthorityRelationship, "from" | "kind">,
  perspectiveSourceId: string,
) {
  const labels = sourceAuthorityRelationshipLabels[relationship.kind];
  return relationship.from === perspectiveSourceId ? labels.outbound : labels.inbound;
}

export function buildSourceAuthorityGraph(
  connectors: ConnectorSnapshot[],
): SourceAuthorityGraph {
  const connectorById = new Map(connectors.map((connector) => [connector.id, connector]));
  const nodes: SourceAuthorityNode[] = connectors.map((connector) => ({
    id: connector.id,
    label: connector.name,
    kind: connectorNodeKind(connector),
    category: connector.category,
    connectorId: connector.id,
    parentConnectorId: null,
    url: connector.docsUrl,
    status: connector.status,
    description: connector.headline,
  }));
  const relationships: SourceAuthorityRelationship[] = [];

  connectors.forEach((connector) => {
    if (!connector.person) return;
    connector.person.profiles
      .filter(profileIsPublishingSurface)
      .forEach((profile) => {
        const id = surfaceId(connector.id, profile.platform);
        nodes.push({
          id,
          label: profileLabel(connector.name, profile),
          kind: profile.platform === "Official" ? "website" : "account",
          category: "person",
          connectorId: null,
          parentConnectorId: connector.id,
          url: profile.url,
          status: "registered",
          description: `${profile.platform} publishing surface associated with ${connector.name}.`,
        });
        relationships.push({
          id: relationshipId(connector.id, "operates", id),
          from: connector.id,
          to: id,
          kind: "operates",
          confidence: "medium",
          state: "registered",
          rationale: `The profile is registered under the ${connector.name} human source. Account control has not been independently re-verified by the engine.`,
          evidence: [{
            type: "first_party_surface",
            label: `${profile.platform} profile registered for ${connector.name}`,
            url: profile.url,
            observedAt: REGISTERED_AT,
          }],
        });
      });
  });

  relationshipSeeds.forEach((seed) => {
    const from = connectorById.get(seed.from);
    const to = connectorById.get(seed.to);
    if (!from || !to) return;
    const evidenceConnector = seed.evidenceSide === "from" ? from : to;
    const roleEvidence = seed.evidenceSide === "from" ? firstRoleEvidence(from) : null;
    relationships.push({
      id: relationshipId(seed.from, seed.kind, seed.to),
      from: seed.from,
      to: seed.to,
      kind: seed.kind,
      confidence: seed.confidence,
      state: seed.state ?? "registered",
      rationale: seed.rationale,
      evidence: [{
        type: seed.evidenceSide === "from" ? "official_role" : "registry_mapping",
        label: roleEvidence?.label ?? `${from.name} → ${to.name} registry mapping`,
        url: roleEvidence?.url ?? evidenceConnector.docsUrl,
        observedAt: REGISTERED_AT,
      }],
    });
  });

  const nodeIds = new Set(nodes.map((node) => node.id));
  const uniqueRelationships = Array.from(
    new Map(relationships.map((relationship) => [relationship.id, relationship])).values(),
  ).filter((relationship) => nodeIds.has(relationship.from) && nodeIds.has(relationship.to));
  const linkedNodeIds = new Set(
    uniqueRelationships.flatMap((relationship) => [relationship.from, relationship.to]),
  );
  const humanNodes = nodes.filter((node) => node.kind === "human");
  const unlinkedHumanNodeIds = humanNodes
    .filter((node) => !linkedNodeIds.has(node.id))
    .map((node) => node.id);

  return {
    schemaVersion: 1,
    nodes,
    relationships: uniqueRelationships,
    coverage: {
      humanNodeCount: humanNodes.length,
      linkedHumanNodeCount: humanNodes.length - unlinkedHumanNodeIds.length,
      unlinkedHumanNodeIds,
    },
  };
}
